import {useCallback, useMemo} from 'react';
import {sendGtagEvent} from 'hooks/analytics/gtag/sendGtagEvent';
import {
    EventAction,
    EventCategory,
    EventCurrencyCodes,
    EventLabel,
    EventListName,
    EventNames,
} from 'modules/google-analytics/constants';
import {useGtagEventLocation} from 'hooks/analytics/gtag/helpers/useGtagEventLocation';
import {useRouter} from 'next/router';
import {getProductEcommerceData} from 'modules/product-card/analytics/gtag/helpers';
import {ISearchData} from 'modules/search-popup/models';

export const useSearchGtagEvents = () => {
    const {eventLocation} = useGtagEventLocation();
    const {asPath} = useRouter();

    const handleSearchClickGtag = useCallback(() => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Search,
            eventAction: EventAction.SearchActivate,
            eventLabel: EventLabel.SearchActivate,
            eventContext: [
                {
                    pageType: eventLocation,
                    pagePath: asPath,
                },
            ],
        });
    }, [asPath, eventLocation]);

    const handleHistoryClickGtag = useCallback((termHistory: string, index: number) => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Search,
            eventAction: EventAction.SearchTermHistoryClick,
            eventLabel: EventLabel.SearchTermHistoryClick,
            eventContext: [
                {
                    termHistory,
                    termHistoryPosition: String(index + 1),
                },
            ],
        });
    }, []);

    const handleSuggestionClickGtag = useCallback((termSuggest: string, index: number) => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Search,
            eventAction: EventAction.SearchTermSuggestClick,
            eventLabel: EventLabel.SearchTermSuggestClick,
            eventContext: [
                {
                    termSuggest,
                    termSuggestPosition: String(index + 1),
                },
            ],
        });
    }, []);

    const handleSuggestProductClickGtag = useCallback((product: ISearchData['products'][number], term: string = '') => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Search,
            eventAction: EventAction.SearchSuggestClick,
            eventLabel: EventLabel.SearchSuggestClick,
            eventContext: [
                {
                    term: term || 'empty_query',
                },
            ],
            ecommerce: {
                currencyCode: EventCurrencyCodes.RUB,
                click: {
                    actionField: {
                        list: EventListName.SearchRecommendations,
                    },
                    products: [
                        {
                            ...getProductEcommerceData(product),
                            list: EventListName.SearchRecommendations,
                        },
                    ],
                },
            },
        });
    }, []);

    const handleSuggestProductAddGtag = useCallback((term: string) => {
        if (!term) {
            return;
        }
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Search,
            eventAction: EventAction.SearchSuggestAdd,
            eventLabel: EventLabel.SearchSuggestAdd,
            eventContext: [
                {
                    term: term || 'empty_query',
                },
            ],
        });
    }, []);

    const handleSearchError = useCallback((term: string, statusCode: any) => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Search,
            eventAction: EventAction.SearchError,
            eventLabel: String(statusCode),
            eventContext: [
                {
                    term: term || 'empty_query',
                },
            ],
        });
    }, []);

    const handleSearchResult = useCallback((term: string, results: number) => {
        sendGtagEvent({
            event: EventNames.OWOX,
            eventCategory: EventCategory.Search,
            eventAction: EventAction.SearchResult,
            eventLabel: EventLabel.SearchResult,
            eventContext: [
                {
                    term: term || 'empty_query',
                    results,
                },
            ],
        });
    }, []);

    return useMemo(
        () => ({
            handleSearchClickGtag,
            handleHistoryClickGtag,
            handleSuggestionClickGtag,
            handleSuggestProductClickGtag,
            handleSuggestProductAddGtag,
            handleSearchError,
            handleSearchResult,
        }),
        [
            handleSearchClickGtag,
            handleHistoryClickGtag,
            handleSuggestionClickGtag,
            handleSuggestProductClickGtag,
            handleSuggestProductAddGtag,
            handleSearchError,
            handleSearchResult,
        ],
    );
};
